import { useEffect, useRef } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import styled, { css, keyframes } from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';

const messages = defineMessages({
  newBadge: {
    id: 'Challenges.ClaimBadgeAnimation.BadgeUnlocked.newBadge',
    defaultMessage: 'New Badge',
  },
  unlocked: {
    id: 'Challenges.ClaimBadgeAnimation.BadgeUnlocked.unlocked',
    defaultMessage: 'Unlocked!',
  },
});

const ANIMATION_DURATION = 0.6;
const PAUSE_TIME = 0.3;
const DELAY_BETWEEN_WORDS = 0.06;
const UNLOCKED_DURATION = 0.4;
const DELAY_BEFORE_UNLOCKED = 0.2;

export const TOTAL_ANIMATION_DURATION =
  DELAY_BEFORE_UNLOCKED + ANIMATION_DURATION + PAUSE_TIME + UNLOCKED_DURATION;

const Root = styled(Vertical).attrs({ center: true })`
  position: absolute;
  inset: 0;
`;
const fadeIn = keyframes`
0% {
  opacity: 0;
  transform: translateY(400px);
}
100% {
  opacity: 1;
  transform: translateY(0);
}`;
const fadeOut = keyframes`
0% {
  opacity: 1;
  transform: translateY(0);
}
100% {
  opacity: 0;
  transform: translateY(-400px);
}`;

const animatedCss = css`
  animation:
    var(--c-cb-ease-out) backwards ${fadeIn},
    var(--c-cb-ease-in-xl) forwards ${fadeOut};
`;
const NewBadge = styled.p`
  display: inline-flex;
  gap: var(--quadruple-unit);
  justify-content: center;
  text-transform: uppercase;
  font-size: 6vmin;

  span {
    ${animatedCss}
  }
`;
const Unlocked = styled.p`
  text-transform: uppercase;
  font-family:
    Druk Wide,
    sans-serif;
  font-weight: 400;
  font-size: 8vmin;

  ${animatedCss}
`;

export const BadgeUnlocked = ({ onEnd }: { onEnd: () => void }) => {
  const { formatMessage } = useIntl();
  const ref = useRef<HTMLDivElement>(null);

  const newBadgeString = formatMessage(messages.newBadge);
  const newBadgeWords = newBadgeString.split(' ');

  useEffect(() => {
    const timer = setTimeout(() => {
      onEnd();
    }, TOTAL_ANIMATION_DURATION * 1000);
    return () => clearTimeout(timer);
  }, [onEnd]);

  return (
    <Root ref={ref}>
      <Vertical gap={2} className="text-center">
        <NewBadge>
          {newBadgeWords.map((word, index) => {
            const fadeDuration =
              ANIMATION_DURATION -
              (newBadgeWords.length - 1) * DELAY_BETWEEN_WORDS;
            const delay = index * DELAY_BETWEEN_WORDS;

            return (
              <span
                key={word}
                style={{
                  animationDuration: `${fadeDuration}s, ${fadeDuration}s`,
                  animationDelay: `${delay}s, ${
                    delay + ANIMATION_DURATION + PAUSE_TIME
                  }s`,
                }}
              >
                {word}
              </span>
            );
          })}
        </NewBadge>
        <Unlocked
          style={{
            animationDuration: `${UNLOCKED_DURATION}s, ${UNLOCKED_DURATION}s`,
            animationDelay: `${DELAY_BEFORE_UNLOCKED}s, ${
              DELAY_BEFORE_UNLOCKED + ANIMATION_DURATION + PAUSE_TIME
            }s`,
          }}
        >
          <FormattedMessage {...messages.unlocked} />
        </Unlocked>
      </Vertical>
    </Root>
  );
};
